import './App.css';
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';

const GREEN = '#118C4F';
const GREEN_DARK = '#0d7040';

function App() {
  const [billContents, setBillContents] = useState([]);
  const [price, setPrice] = useState(0.00);
  const [name, setName] = useState('');
  const [tip, setTip] = useState(0.00);
  const [tax, setTax] = useState(0.00);
  const [grandTotal, setGrandTotal] = useState(0.00);
  const [error, setError] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingPrice, setEditingPrice] = useState('');

  useEffect(() => {
    calculateTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billContents]);

  const addPerson = () => {
    if (name.trim() === '') {
      setError('Name cannot be empty');
    } else {
      setError('');
      setBillContents([
        ...billContents,
        { name: name, price: formatToDecimal(price) },
      ]);
      setName('');
      setPrice(0.00);
    }
  };

  const formatToDecimal = (num) => {
    return parseFloat(num);
  };

  const startEditingPrice = (index) => {
    setEditingIndex(index);
    setEditingPrice(billContents[index].price);
  };

  const commitPrice = (index) => {
    const updated = [...billContents];
    updated[index] = { ...updated[index], price: formatToDecimal(editingPrice) || 0 };
    setEditingIndex(null);
    setBillContents(updated);
  };

  const removeItemByIndex = (index) => {
    const updatedBillContents = [...billContents];
    updatedBillContents.splice(index, 1);
    setBillContents(updatedBillContents);
  };

  const calculateTotal = () => {
    let total = 0.00;
    let grandTotal = 0.00;
    billContents.map((item) => {
      return total += item.price;
    });

    let newBillContents = billContents.map((item) => {
      let tipTotal = ((item.price / total) * tip);
      let taxTotal = ((item.price / total) * tax);
      let totalAfterTipAndTax = item.price + tipTotal + taxTotal;
      grandTotal += totalAfterTipAndTax;
      return { ...item, tip: tipTotal.toFixed(2), tax: taxTotal.toFixed(2), total: totalAfterTipAndTax.toFixed(2) };
    });

    setBillContents(newBillContents);
    setGrandTotal(grandTotal);
  };

  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

  return (
    <div className="App">
      <div className="app-card">

        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)`,
            borderRadius: '16px 16px 0 0',
            px: 3,
            py: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <ReceiptLongIcon sx={{ color: 'white', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, lineHeight: 1.2 }}>
              Bill Splitter
            </Typography>
          </Box>
        </Box>

        {/* Main Card */}
        <Card elevation={3} sx={{ borderRadius: '0 0 16px 16px' }}>
          <CardContent sx={{ p: 3 }}>

            {/* Add Person Section */}
            <Typography variant="overline" color="text.secondary" fontWeight={600}>
              Add Person
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1, mb: 3 }}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={error !== ''}
                helperText={error}
                size="small"
                sx={{ flex: 2 }}
              />
              <TextField
                label="Amount"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onFocus={(e) => e.target.select()}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <Button
                onClick={addPerson}
                variant="contained"
                startIcon={<PersonAddIcon />}
                sx={{
                  backgroundColor: GREEN,
                  ':hover': { backgroundColor: GREEN_DARK },
                  whiteSpace: 'nowrap',
                  px: 2.5,
                }}
              >
                Add
              </Button>
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Tip & Tax Section */}
            <Typography variant="overline" color="text.secondary" fontWeight={600}>
              Tip & Tax
            </Typography>
            <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
              <TextField
                label="Tip"
                type="number"
                value={tip}
                onChange={(e) => setTip(e.target.value)}
                onFocus={(e) => e.target.select()}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                label="Tax"
                type="number"
                value={tax}
                onChange={(e) => setTax(e.target.value)}
                onFocus={(e) => e.target.select()}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {/* Table Section */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <PeopleAltIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
              <Typography variant="overline" color="text.secondary" fontWeight={600}>
                Split ({billContents.length} {billContents.length === 1 ? 'person' : 'people'})
              </Typography>
            </Stack>

            {billContents.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  px: 2,
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'divider',
                  color: 'text.disabled',
                }}
              >
                <PeopleAltIcon sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">No people added yet. Add someone above to get started.</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Tip</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Tax</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billContents.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td': { border: 0 },
                          '&:hover': { backgroundColor: '#fafafa' },
                        }}
                      >
                        <TableCell>{row.name}</TableCell>
                        <TableCell align="right" sx={{ minWidth: 100 }}>
                          {editingIndex === index ? (
                            <TextField
                              type="number"
                              value={editingPrice}
                              onChange={(e) => setEditingPrice(e.target.value)}
                              onBlur={() => commitPrice(index)}
                              onKeyDown={(e) => { if (e.key === 'Enter') commitPrice(index); if (e.key === 'Escape') setEditingIndex(null); }}
                              onFocus={(e) => e.target.select()}
                              size="small"
                              autoFocus
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                              sx={{ width: 100 }}
                            />
                          ) : (
                            <Tooltip title="Click to edit">
                              <Box
                                component="span"
                                onClick={() => startEditingPrice(index)}
                                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: GREEN } }}
                              >
                                {fmt(row.price)}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt(row.tip)}</TableCell>
                        <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{fmt(row.tax)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(row.total)}</TableCell>
                        <TableCell align="right" sx={{ pr: 0.5 }}>
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              onClick={() => removeItemByIndex(index)}
                              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Grand Total */}
            {billContents.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: `${GREEN}12`,
                  border: `1px solid ${GREEN}30`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body1" fontWeight={600} color={GREEN}>
                  Grand Total
                </Typography>
                <Typography variant="h6" fontWeight={700} color={GREEN}>
                  {fmt(grandTotal)}
                </Typography>
              </Box>
            )}

          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default App;
